import React from 'react';
import { format } from 'date-fns';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Iter-46aj — 7-day strip selector for the client Schedule page.
 *
 * Pure presentational: keeps no state of its own. Parent owns the week
 * window (``days``), the active selection (``selectedDay``), the per-day
 * class lookup (``classes`` keyed by YYYY-MM-DD), and the navigation
 * handlers. Clicking a day fires ``onSelectDay`` AND scrolls the matching
 * ``#day-section-<dayStr>`` element into view so the page jumps to that
 * day's class list — the side effect lives here so the parent doesn't
 * need to know about DOM ids.
 */
export const WeekStrip = ({
  days,
  selectedDay,
  onSelectDay,
  classes,
  onPrevWeek,
  onNextWeek,
}) => {
  const selectedDateStr = selectedDay ? format(selectedDay, 'yyyy-MM-dd') : '';
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={onPrevWeek}
            data-testid="prev-week-btn"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>

          <div className="flex-1 flex gap-2 overflow-x-auto pb-2">
            {days.map((day, i) => {
              const dayStr = format(day, 'yyyy-MM-dd');
              const isSelected = dayStr === selectedDateStr;
              const dayClsCount = (classes[dayStr] || []).length;
              return (
                <button
                  key={dayStr}
                  onClick={() => {
                    onSelectDay(day);
                    const el = document.getElementById(`day-section-${dayStr}`);
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  data-testid={`day-btn-${i}`}
                  className={`flex-shrink-0 flex flex-col items-center px-4 py-3 rounded-xl transition-all ${
                    isSelected
                      ? 'bg-primary text-primary-foreground shadow-lg'
                      : 'hover:bg-muted border'
                  }`}
                >
                  <span className="text-xs uppercase">{format(day, 'EEE')}</span>
                  <span className="text-2xl font-bold">{format(day, 'd')}</span>
                  <span className="text-xs">{format(day, 'MMM')}</span>
                  {dayClsCount > 0 && (
                    <Badge variant={isSelected ? 'secondary' : 'outline'} className="mt-1 text-xs">
                      {dayClsCount} classes
                    </Badge>
                  )}
                </button>
              );
            })}
          </div>

          <Button
            variant="outline"
            size="icon"
            onClick={onNextWeek}
            data-testid="next-week-btn"
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
